/* global runtime */

const receiptInboxBackendEntries = () => safeArray(runtime.appState?.receiptInbox);

// Risikoflaggene fra analysen bærer engelske utviklermeldinger, og de samme
// manglene utledes en gang til av sjekkene under. Uten denne oversettelsen sto
// begge versjonene i innboksen — «Missing payment source. ... Mangler
// betalingskilde.» — av samme mangel.
const RECEIPT_REVIEW_FLAG_TEXT = {
  analysis_failed: 'Bilagsanalysen feilet.',
  missing_date: 'Mangler dato.',
  missing_amount: 'Mangler beløp.',
  missing_posting_type: 'Mangler bokføringstype.',
  free_posting_review: 'Fri postering må kontrolleres manuelt.',
  collection_notice_requires_original: 'Inkassovarselet må knyttes til originalbilaget. Hovedkravet skal ikke kostnadsføres eller MVA-føres på nytt.',
  missing_payment_source: 'Mangler betalingskilde.',
  missing_supplier: 'Mangler leverandør.',
  missing_invoice_number: 'Mangler fakturanummer.',
  missing_cost_account: 'Mangler kostnadskonto.',
  line_total_mismatch: 'Bilagslinjene summerer ikke til totalsummen.',
  vat_total_mismatch: 'MVA-beløpet på linjene stemmer ikke med bilaget.',
  payment_evidence_review: 'Betalingskilden er funnet, men oppgjørskontoen må kontrolleres.',
  low_confidence: 'Lav sikkerhet på analysen.',
};

// ------------------------------------------------- koblet OG ferdig snakket om
//
// Et bilag som er koblet til en banklinje hoerer til den linjen: den sier hva
// kjopet var, og bilaget er dokumentasjonen. Derfor skjuler maanedskontrollen og
// innbokspanelet dem — ellers ville det samme kjopet staatt to steder i samme
// arbeidsflate.
//
// Men «koblet» og «ferdig» er ikke det samme. Er banklinjen alt matchet mot en
// bokfort post, eller er filen en dobbeltopplasting, staar bilaget paa
// `needs-review` — og da BLE det skjult mens agenten fortsatte aa telle det.
// Maaneden sa «2 bilag i innboksen» og arbeidsflaten sa «0 bilag», og det fantes
// ingen vei til de to.
//
// Skjulingen gjelder derfor bare de koblede bilagene det ikke er noe mer aa si
// om. Et koblet bilag som trenger kontroll skal fortsatt vaere til stede der
// noen kan gjore noe med det. Regelen bor spoerres om her og ikke skrives om paa
// nytt: tre kopier av den var nettopp det som lot tallene sprike.
const receiptBankLinkedResolved = (linkedInfo, review) =>
  Boolean(linkedInfo) && review?.status !== 'needs-review';

const receiptInboxReview = (entry, paymentSourceHint) => {
  if (receiptInboxEntryIsTrashed(entry)) return { status: 'trashed', label: 'I papirkurv', reasons: [] };
  if (entry.status === 'booked') return { status: 'booked', label: 'Bokført', reasons: [] };
  const analysis = entry.receiptAnalysis;
  if (!analysis || analysis.status === 'pending' || analysis.status === 'queued') {
    // Bare `pending` betyr at analysen faktisk kjorer. Et bilag uten analysefelt i
    // det hele tatt — slik e-postbilag ble opprettet — sto og lot som om det var
    // arbeid i gang, uten at noe noensinne var startet.
    return {
      status: 'analyzing',
      label: analysis?.status === 'pending' ? 'Analyseres' : 'Venter på analyse',
      reasons: [],
    };
  }
  // Et bekreftet banktreff er hardt bevis for hvor betalingen gikk fra, og
  // sterkere enn analysens egen gjetning. Da er «mangler betalingskilde» ikke
  // lenger sant, verken som flagg fra analysen eller som egen sjekk under.
  // Hardt bevis: en betaling funnet i banken, eller et kort som er registrert paa
  // en av foretakets kontoer. Begge sier hvor pengene faktisk kom fra, og ingen
  // av dem trenger en merknad.
  const bankVerifiedPaymentSource = (paymentSourceHint?.match?.confidence === 'high'
    || paymentSourceHint?.confidence === 'high'
    || paymentSourceHint?.origin === 'payment-card')
    && paymentSourceHint?.requiresReview !== true
    && Boolean(paymentSourceHint?.accountCode);
  // En ubetalt leverandorfaktura har ingen betalingskilde, og skal ikke ha en:
  // den star som gjeld til den betales. Da er «mangler betalingskilde» feil
  // sporsmaal, ikke en mangel.
  const unpaidSupplierInvoice = analysis?.documentType === 'supplier_invoice' && analysis?.isPaid === false && !bankVerifiedPaymentSource;
  const manualPaymentSource = ['manual', 'manual-at-upload'].includes(analysis?.paymentSourceOrigin)
    && Boolean(String(analysis?.paymentAccountCode || '').trim());
  // De ovrige kildene — et middels banktreff, «ingen betaling i banken»,
  // historikken eller foretakets standardvalg — er gode nok til aa forhandsvelge
  // en konto, men ikke til aa passere i stillhet. De gjor bilaget klart og legger
  // igjen en merknad, slik at valget er synlig for det bokfores.
  const hintedPaymentSource = !bankVerifiedPaymentSource && !unpaidSupplierInvoice && paymentSourceHint?.paymentSource
    ? paymentSourceHint
    : null;
  const paymentSourceResolved = bankVerifiedPaymentSource || unpaidSupplierInvoice || manualPaymentSource || Boolean(hintedPaymentSource);
  const notes = [];
  // Sto kortnummeret paa bilaget, er mangelen handterbar: kortet er ikke
  // registrert. Uten sifrene i teksten er «mangler betalingskilde» en blindvei —
  // brukeren aner ikke om vi ikke leste kortet, eller om kortet ikke er kjent.
  const unregisteredCardLast4 = String(analysis?.cardLast4 || '').replace(/\D/g, '').slice(-4);
  const missingPaymentSourceText = unregisteredCardLast4.length === 4
    ? `Mangler betalingskilde. Kortet **** ${unregisteredCardLast4} er ikke registrert på en bankkonto.`
    : 'Mangler betalingskilde.';
  // Den faktiske arsaken sto i `analysis.error` og ble kastet: hver eneste rad sa
  // «Bilagsanalysen feilet.» uten aa royke at AI-kvoten var brukt opp.
  const analysisFailureText = analysis.status === 'failed'
    ? (analysis.error ? `Bilagsanalysen feilet: ${String(analysis.error).slice(0, 160)}` : 'Bilagsanalysen feilet.')
    : '';
  const reasons = [...safeArray(entry.riskFlags), ...safeArray(analysis.riskFlags)]
    // `foreign_currency` utelates her: valutastatusen avgjøres av
    // `PlummeReceiptCurrency.reviewReason` nedenfor, som ser det omregnede
    // beløpet slik det står nå og ikke slik det sto da flagget ble satt.
    .filter((flag) => flag && flag.code !== 'foreign_currency')
    .filter((flag) => !(paymentSourceResolved && flag.code === 'missing_payment_source'))
    .map((flag) => (flag.code === 'analysis_failed' && analysisFailureText)
      || (flag.code === 'missing_payment_source' && missingPaymentSourceText)
      || RECEIPT_REVIEW_FLAG_TEXT[flag.code] || flag.message)
    .filter(Boolean);
  if (analysisFailureText) reasons.push(analysisFailureText);
  if (analysis.status === 'completed') {
    if (!analysis.receiptDate) reasons.push('Mangler dato.');
    // En dato fram i tid er lest feil. To like Elkjop-bilag ble ikke sett som
    // duplikater fordi det ene sto med 2027 — da hjelper ingen datovindu-regel.
    if (analysis.receiptDate && analysis.receiptDate > todayIso()) {
      reasons.push('Datoen ligger fram i tid. Kontroller at den er lest riktig.');
    }
    // Filnavnet fra en eksport inneholder ofte datoen. Terskelen er to maaneder,
    // ikke dager: filnavnet kan like gjerne bere datoen bilaget ble sendt eller
    // eksportert som datoen det ble kjopt. Det denne skal fange er aarsbommen —
    // «2023-07-14» lest av en fil som heter «fiken-bilag-2026-07-14» — og den er
    // stor nok til aa se uten aa mase om noen dagers avvik.
    const nameDate = String(entry.attachmentName || '').match(/(20\d{2})-(\d{2})-(\d{2})/);
    if (analysis.receiptDate && nameDate && !receiptDatesWithinWindow(analysis.receiptDate, nameDate[0], 60)) {
      reasons.push(`Datoen i filnavnet (${window.PlummeI18n.date(nameDate[0])}) er en annen enn datoen som er lest av bilaget.`);
    }
    if (!(num(analysis.totalGross) > 0)) reasons.push('Mangler beløp.');
    const currencyReason = window.PlummeReceiptCurrency.reviewReason(analysis);
    if (currencyReason) reasons.push(currencyReason);
    if (!analysis.suggestedPostingType || analysis.suggestedPostingType === 'unknown') reasons.push('Mangler bokføringstype.');
    if (analysis.suggestedPostingType === 'free-posting') reasons.push('Fri postering må kontrolleres manuelt.');
    const settlementInvoiceMatches = analysis.documentType === 'settlement_report'
      ? receiptSettlementOpenInvoiceMatchSuggestions(analysis)
      : [];
    const settlementInvoiceMatched = settlementInvoiceMatches.length === 1 || Boolean(analysis.settlementInvoiceId
      && settlementInvoiceMatches.some((match) => String(match.invoiceId || '') === String(analysis.settlementInvoiceId)));
    if (analysis.documentType === 'settlement_report' && typeof analysis.vatRate !== 'number' && !settlementInvoiceMatched) {
      reasons.push('Oppgjøret viser ikke MVA-sats. Kontroller om beløpet gjelder faktura eller annet salg før bokføring.');
    }
    if (analysis.documentType === 'settlement_report' && settlementInvoiceMatches.length > 1 && !settlementInvoiceMatched) {
      reasons.push('Oppgjoret matcher flere apne fakturaer. Velg riktig faktura for bokforing.');
    }
    const storedPaymentAccount = String(analysis.paymentAccountCode || '').trim();
    const cashSource = analysis.suggestedPaymentSource === 'cash';
    if (!paymentSourceResolved && !storedPaymentAccount && !cashSource) reasons.push(missingPaymentSourceText);
    if (hintedPaymentSource?.reason) notes.push(hintedPaymentSource.reason);
    if (hintedPaymentSource?.requiresReview || (hintedPaymentSource && hintedPaymentSource.confidence !== 'high')) {
      reasons.push('Betalingskilden er foreslått, men må kontrolleres.');
    }
    // «Kontant» staar paa bilaget, men det er ikke det samme som at det ble betalt
    // kontant. Serveringsstedene skriver ut en kontantslipp med totalen, og
    // betaler du med kort, kommer kortkvitteringen i tillegg — den forste er da
    // misvisende. Vi har ingen kontroll paa hvilken av dem brukeren fotograferer.
    //
    // Dette er en kontrollgrunn og ikke en merknad: kontant er sjeldent i dag, en
    // merknad blir lest forbi, og feilen bokforer kjopet i en kontantkasse som
    // ikke finnes. Da er ett klikk paa et ekte kontantkjop den billigste prisen.
    if (analysis.suggestedPaymentSource === 'cash' && !manualPaymentSource) {
      reasons.push('Bilaget er merket kontant. Bekreft at det ikke ble betalt med kort.');
    }
    if (unpaidSupplierInvoice) notes.push('Registreres som ubetalt. Betalingen kan registreres nar den er gjort.');
    if (analysis.documentType === 'supplier_invoice' && !analysis.supplierName) reasons.push('Mangler leverandør.');
    else if (analysis.supplierIsOwnBusiness) reasons.push('Leverandøren er lest som ditt eget foretak. Kontroller hvem fakturaen er fra — avsenderen står vanligvis øverst.');
    if (analysis.documentType === 'supplier_invoice' && analysis.supplierName && !findSupplierRecordByName(analysis.supplierName, undefined, analysis.supplierId)) reasons.push(t('inbox.supplierNotSaved'));
    if (analysis.documentType === 'supplier_invoice' && !analysis.invoiceNumber) reasons.push('Mangler fakturanummer.');
    if (analysis.suggestedPostingType === 'expense' && !analysis.suggestedCostAccountCode
      && !safeArray(analysis.purchaseLines).some((line) => line?.costAccountCode)) reasons.push('Mangler kostnadskonto.');
    if (analysis.purchaseLineValidation?.status === 'mismatch') {
      reasons.push(Math.abs(num(analysis.purchaseLineValidation.vatDifference)) > 0.02
        ? 'MVA-beløpet på linjene stemmer ikke med bilaget.'
        : 'Bilagslinjene summerer ikke til totalsummen.');
    }
    if (analysis.reviewReason) reasons.push(analysis.reviewReason);
    if (analysis.confidence === 'low') reasons.push('Lav sikkerhet på analysen.');
    if ((entry.approvalRoute || analysis.approvalRoute) === 'manual_review' && reasons.length === 0 && !paymentSourceResolved) {
      reasons.push('Bilagsanalysen krever kontroll før bokføring.');
    }
  }
  const unique = [...new Set(reasons.map((reason) => String(reason || '').trim()).filter(Boolean))];
  const uniqueNotes = [...new Set(notes.map((note) => String(note || '').trim()).filter(Boolean))];
  if (unique.length) return { status: 'needs-review', label: 'Trenger kontroll', reasons: unique, notes: uniqueNotes };
  return { status: 'ready-to-book', label: 'Klar for bokforing', reasons: [], notes: uniqueNotes };
};

// Samme bilag kan komme inn flere ganger: lastet opp to ganger, eller videresendt
// pa e-post etter at det alt la i innboksen. Filsummen fanger den identiske fila
// uansett hvor lang tid det gikk imellom; analysen fanger den samme kvitteringen
// skannet pa nytt. Et bilag i soppelkassa teller ikke — det er kastet med vilje.
const receiptUploadDuplicateKeys = (entry) => {
  const keys = [];
  const hash = String(entry.fileHash || entry.receiptAnalysis?.fileHash || '').trim().toLowerCase();
  if (hash) keys.push(`file:${hash}`);
  const analysis = entry.receiptAnalysis;
  const date = String(analysis?.receiptDate || '').slice(0, 10);
  const gross = num(analysis?.totalGross);
  const supplier = normalizedPartyName(analysis?.supplierName);
  if (analysis?.status === 'completed' && gross > 0 && supplier) {
    // Datoen er med vilje ute av noekkelen. Det samme kjopet kom inn to ganger —
    // en kvittering paa e-post og en utskrift i lomma — og datoen ble lest ulikt
    // paa de to. Krevde vi lik dato, var det nettopp de tilfellene som slapp
    // gjennom. Dagene sammenliknes i stedet naar treffet vurderes.
    keys.push(`content:${supplier}|${gross.toFixed(2)}`);
  }
  return keys;
};

// Samme leverandor og samme sum innenfor en uke er det samme kjopet oftere enn
// det er to kjop. Lengre enn det, og en fast ukentlig utgift ville begynt aa
// varsle om seg selv.
const RECEIPT_DUPLICATE_DAY_WINDOW = 7;

const receiptDatesWithinWindow = (left, right, days = RECEIPT_DUPLICATE_DAY_WINDOW) => {
  const a = String(left || '').slice(0, 10);
  const b = String(right || '').slice(0, 10);
  if (!a || !b) return true;
  const diff = Math.abs(new Date(`${a}T00:00:00Z`) - new Date(`${b}T00:00:00Z`));
  return Number.isFinite(diff) && diff <= days * 86400000;
};

const buildReceiptUploadDuplicateIndex = (entries) => {
  const byKey = new Map();
  entries.forEach((entry) => {
    if (receiptInboxEntryIsTrashed(entry) || entry.status === 'booked') return;
    receiptUploadDuplicateKeys(entry).forEach((key) => {
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(entry);
    });
  });
  return byKey;
};

const receiptUploadDuplicateFor = (entry, index) => {
  if (receiptInboxEntryIsTrashed(entry) || entry.status === 'booked') return undefined;
  const matches = new Map();
  let sameFile = false;
  receiptUploadDuplicateKeys(entry).forEach((key) => {
    safeArray(index.get(key)).forEach((candidate) => {
      if (candidate.id === entry.id) return;
      // Identisk fil er identisk uansett dato. For innholdstreffet ma dagene ligge
      // naer hverandre, ellers ville to like kjop et halvt ar fra hverandre matchet.
      if (key.startsWith('content:')
        && !receiptDatesWithinWindow(entry.receiptAnalysis?.receiptDate, candidate.receiptAnalysis?.receiptDate)) return;
      matches.set(candidate.id, candidate);
      if (key.startsWith('file:')) sameFile = true;
    });
  });
  if (!matches.size) return undefined;
  return {
    reason: sameFile ? 'file' : 'content',
    count: matches.size,
    booked: [...matches.values()].filter((candidate) => candidate.status === 'booked').length,
    ids: [...matches.keys()],
  };
};

const receiptSettlementAlreadyBookedMatch = (entry, state = runtime.appState) => {
  if (receiptInboxEntryIsTrashed(entry) || entry?.status === 'booked') return null;
  const analysis = entry?.receiptAnalysis || {};
  if (analysis?.status !== 'completed' || analysis?.documentType !== 'settlement_report') return null;
  const settlementId = String(analysis.settlementId || '').trim();
  const gross = num(analysis.settlementGrossAmount || analysis.totalGross);
  const fee = Math.max(0, num(analysis.settlementFeeAmount));
  const payout = receiptSettlementExpectedPayoutAmount(analysis);
  if (!settlementId || !(gross > 0) || !(payout > 0)) return null;
  const payment = safeArray(state?.payments).find((candidate) => {
    if (candidate?.status === 'reversed' || candidate?.direction === 'outgoing') return false;
    if (candidate?.receiptInboxEntryId && candidate.receiptInboxEntryId === entry.id) return false;
    const candidateBankAmount = candidate.bankAmount === undefined || candidate.bankAmount === null || candidate.bankAmount === ''
      ? Math.max(0, num(candidate.amount) - Math.max(0, num(candidate.feeAmount)))
      : num(candidate.bankAmount);
    if (Math.abs(num(candidate.amount) - gross) > 0.01) return false;
    if (Math.abs(candidateBankAmount - payout) > 0.01) return false;
    if (Math.abs(Math.max(0, num(candidate.feeAmount)) - fee) > 0.01) return false;
    return receiptSettlementDescriptionHasId(`${candidate.reference || ''} ${candidate.note || ''} ${candidate.bankTrace?.lineDescription || ''}`, settlementId);
  });
  if (payment) return { type: 'payment', id: payment.id, documentId: payment.documentId };
  const bankLine = safeArray(state?.bankStatements).flatMap((statement) =>
    safeArray(statement?.lines).map((line) => ({ statement, line })),
  ).find(({ line }) => {
    if (!line?.matchedEntryId) return false;
    if (line.receiptInboxEntryId && line.receiptInboxEntryId === entry.id) return false;
    if (Math.abs(num(line.amount) - payout) > 0.01) return false;
    return receiptSettlementDescriptionHasId(line.description, settlementId);
  });
  return bankLine ? { type: 'bank-line', statementId: bankLine.statement.id, lineId: bankLine.line.id, entryId: bankLine.line.matchedEntryId } : null;
};

const receiptInboxAlreadyBookedMatch = (entry, state = runtime.appState) => {
  const settlementMatch = receiptSettlementAlreadyBookedMatch(entry, state);
  if (settlementMatch) return { ...settlementMatch, documentType: 'settlement_report' };
  if (receiptInboxEntryIsTrashed(entry) || entry?.status === 'booked') return null;
  const analysis = entry?.receiptAnalysis || {};
  if (analysis?.status !== 'completed' || analysis?.documentType === 'settlement_report') return null;
  const gross = Math.abs(num(window.PlummeReceiptCurrency?.grossNok?.(analysis) ?? analysis.totalGross));
  const date = String(analysis.receiptDate || '').slice(0, 10);
  if (!date || !(gross > 0)) return null;
  const expenseMatch = safeArray(state?.expenses).find((expense) =>
    isActiveExpense(expense)
    && !expense.annulledAt
    && String(expense.id || '') !== String(entry.linkedEntryId || '')
    && String(expense.receiptInboxEntryId || '') !== String(entry.id || '')
    && String(expense.date || '').slice(0, 10) === date
    && Math.abs(dashboardGrossExpense(expense) - gross) <= RECEIPT_DUPLICATE_AMOUNT_TOLERANCE);
  if (expenseMatch) return { type: 'expense', id: expenseMatch.id, documentType: analysis.documentType || 'receipt' };
  const bankLineMatch = safeArray(state?.bankStatements).flatMap((statement) =>
    safeArray(statement?.lines).map((line) => ({ statement, line })),
  ).find(({ line }) => {
    if (!line?.matchedEntryId || line?.receiptInboxEntryId === entry.id) return false;
    if (num(line.amount) >= 0) return false;
    if (Math.abs(Math.abs(num(line.amount)) - gross) > RECEIPT_DUPLICATE_AMOUNT_TOLERANCE) return false;
    return receiptDatesWithinWindow(date, line.date);
  });
  return bankLineMatch ? {
    type: 'bank-line',
    statementId: bankLineMatch.statement.id,
    lineId: bankLineMatch.line.id,
    entryId: bankLineMatch.line.matchedEntryId,
    documentType: analysis.documentType || 'receipt',
  } : null;
};

const receiptInboxReviewWithContext = (entry, paymentSourceHint, duplicateUpload, linkedInfo, alreadyBookedMatch) => {
  let review = receiptInboxReview(entry, paymentSourceHint);
  const open = entry?.status !== 'booked' && !receiptInboxEntryIsTrashed(entry);
  const needsReview = (reasons) => ({ status: 'needs-review', label: 'Trenger kontroll', reasons: [...new Set(reasons)], notes: review?.notes || [] });
  if (alreadyBookedMatch && open) {
    review = needsReview([alreadyBookedMatch.documentType === 'settlement_report'
      ? 'Oppgjørsrapporten ser allerede bokført ut. Kontroller koblingen og flytt duplikatet til papirkurv hvis dette er samme oppgjør.'
      : 'Bilaget ser allerede bokført ut. Kontroller koblingen og flytt duplikatet til papirkurv hvis dette er samme kjøp.',
      ...(review?.reasons || [])]);
  }
  if (linkedInfo?.booked && linkedInfo?.suggested && open) {
    review = needsReview([...(review?.reasons || []),
      'Banklinjen er allerede matchet mot en bokført post. Åpne koblingen i Bank eller flytt duplikatbilaget til papirkurv.']);
  }
  return window.PlummeReceiptInboxDuplicateReview?.applyExactFileGuard?.(review, duplicateUpload, entry?.status) || review;
};

const receiptInboxPossibleDuplicateExpenses = (entry) => {
  if (receiptInboxEntryIsTrashed(entry) || entry?.status === 'booked') return [];
  const analysis = entry?.receiptAnalysis || {};
  if (analysis.status !== 'completed') return [];
  const grossNok = window.PlummeReceiptCurrency?.grossNok?.(analysis);
  const grossAmount = grossNok == null ? num(analysis.totalGross) : grossNok;
  return findPossibleDuplicateExpenses({
    date: analysis.receiptDate,
    grossAmount,
    supplierName: analysis.supplierName,
    excludeExpenseId: entry.linkedEntryId,
  });
};

const receiptInboxLinkedBankGrossNok = (analysis, linkedInfo) => {
  if (analysis?.documentType === 'settlement_report') return null;
  if (String(analysis?.currency || 'NOK').toUpperCase() !== 'NOK') return null;
  const gross = Math.abs(num(linkedInfo?.bankTrace?.lineAmount ?? linkedInfo?.lineAmount));
  return gross > 0.005 ? backendRoundMoney(gross) : null;
};

const receiptInboxConfirmedVehicleVatPatch = (analysis, gross, state = runtime.appState) => {
  const accountCode = String(analysis?.suggestedCostAccountCode || '').replace(/\D/g, '').slice(0, 4);
  if (!['7000', '7020'].includes(accountCode)) return {};
  if (String(state?.settings?.country || '').toUpperCase() !== 'NO') return {};
  if (state?.settings?.vatRegistered !== true) return {};
  const period = window.PlummeVehicleCostPolicy?.activeForDate?.(state?.settings?.vehicleCostPolicy, analysis?.receiptDate);
  if (period?.mode !== 'vat_deductible' || period?.responsibilityAccepted !== true) return {};
  const documentedVatAmount = Number(analysis?.documentVatAmount ?? analysis?.vatAmount);
  if (!(gross > 0) || (Number.isFinite(documentedVatAmount) && documentedVatAmount > 0)) return {};
  const documentedVatRate = Number(analysis?.documentVatRate ?? analysis?.vatRate);
  const vatRate = [25, 15, 12].includes(documentedVatRate) ? documentedVatRate : 25;
  const vatAmount = backendRoundMoney((gross * vatRate) / (100 + vatRate));
  const documentNetAmount = backendRoundMoney(gross - vatAmount);
  return {
    vatRate,
    vatAmount,
    documentVatRate: analysis?.documentVatRate || vatRate,
    documentVatAmount: analysis?.documentVatAmount || vatAmount,
    documentNetAmount: analysis?.documentNetAmount || documentNetAmount,
    vatTreatment: 'standard',
    vehicleCostReminder: analysis?.vehicleCostReminder || 'Kun dersom foretaket eier eller leaser kjøretøy.',
    vehicleCostPolicyDecision: analysis?.vehicleCostPolicyDecision || {
      automatic: true,
      deductInputVat: true,
      reasonCode: 'vehicle_policy_vat_deductible',
      productSupport: 'supported',
      period,
    },
  };
};

const receiptInboxAnalysisWithLinkedContext = (entry, linkedInfo, state = runtime.appState) => {
  const analysis = entry?.receiptAnalysis;
  if (!analysis || analysis.status !== 'completed') return analysis;
  const bankGross = receiptInboxLinkedBankGrossNok(analysis, linkedInfo);
  const base = bankGross
    ? stripUndefinedDeep({
        ...analysis,
        totalGross: bankGross,
        totalGrossNok: bankGross,
        bankDisplayAmount: {
          source: 'linked-bank-line',
          fromTotalGross: num(analysis.totalGross),
          toTotalGross: bankGross,
          statementId: linkedInfo?.statementId,
          lineId: linkedInfo?.lineId,
        },
      })
    : analysis;
  const gross = receiptAnalysisBookableGrossNok(base, linkedInfo) ?? window.PlummeReceiptCurrency?.grossNok?.(base) ?? num(base.totalGross);
  const vatPatch = receiptInboxConfirmedVehicleVatPatch(base, gross, state);
  return Object.keys(vatPatch).length ? stripUndefinedDeep({ ...base, ...vatPatch }) : base;
};

const buildReceiptInboxModel = () => {
  const state = mergeState(runtime.appState);
  const backendEntries = receiptInboxBackendEntries().filter((entry) => entry.source !== 'editor-upload');
  const linked = bankLinkedReceiptIndex(state);
  const duplicateIndex = buildReceiptUploadDuplicateIndex(backendEntries);
  const entries = backendEntries
    .map((entry) => {
      const linkedInfo = linked.get(entry.id);
      const modelAnalysis = receiptInboxAnalysisWithLinkedContext(entry, linkedInfo, state);
      const modelEntry = modelAnalysis === entry.receiptAnalysis ? entry : { ...entry, receiptAnalysis: modelAnalysis };
      const paymentSourceHint = bankLinkedReceiptPaymentSourceHint(linkedInfo) || window.PlummeReceiptPaymentSource.suggest(state, modelAnalysis);
      const duplicateUpload = receiptUploadDuplicateFor(entry, duplicateIndex);
      const alreadyBookedMatch = receiptInboxAlreadyBookedMatch(modelEntry, state);
      const settlementBookedMatch = alreadyBookedMatch?.documentType === 'settlement_report' ? alreadyBookedMatch : null;
      const possibleDuplicateExpenses = receiptInboxPossibleDuplicateExpenses(modelEntry);
      const review = receiptInboxReviewWithContext(modelEntry, paymentSourceHint, duplicateUpload, linkedInfo, alreadyBookedMatch);
      return {
        ...mapReceiptToUi(modelEntry),
        linkedBankLine: linkedInfo,
        alreadyBookedMatch,
        settlementBookedMatch,
        review,
        bankLinkedResolved: receiptBankLinkedResolved(linkedInfo, review),
        paymentSourceHint,
        duplicateUpload,
        possibleDuplicateExpenses,
      };
    })
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const active = entries.filter((entry) => !receiptInboxEntryIsTrashed(entry) && entry.status !== 'booked' && entry.status !== 'linked-supporting-document');
  const trash = entries.filter((entry) => receiptInboxEntryIsTrashed(entry));
  const booked = entries.filter((entry) => entry.status === 'booked');
  return {
    entries,
    active,
    trash,
    ready: active.filter((entry) => entry.review?.status === 'ready-to-book'),
    needsReview: active.filter((entry) => entry.review?.status === 'needs-review'),
    analyzing: active.filter((entry) => entry.review?.status === 'analyzing'),
    booked,
  };
};

const receiptWorkflowPatch = (entry, status, message) => {
  const at = nowIso();
  const stepId = status === 'booked' ? 'book_after_approval' : 'extract_document_data';
  return stripUndefinedDeep({
    receiptWorkflowStatus: status,
    approvalRoute: entry.approvalRoute || (status === 'booked' ? 'quick_approval' : 'manual_review'),
    workflowSteps: [
      ...safeArray(entry.workflowSteps),
      {
        id: stepId,
        status: status === 'failed' ? 'failed' : 'completed',
        completedAt: at,
        ...(typeof entry.confidenceScore === 'number' ? { confidenceScore: entry.confidenceScore } : {}),
      },
    ],
    workflowAuditTrail: [
      ...safeArray(entry.workflowAuditTrail),
      stripUndefinedDeep({
        stepId,
        action: status === 'booked' ? 'booked' : status,
        status,
        at,
        source: 'plumme',
        message,
      }),
    ],
  });
};
const patchReceiptInboxEntry = async (entryId, patch = {}) => {
  let updated = null;
  await updateStateAndPersist((state) => {
    const receiptInbox = safeArray(state.receiptInbox).map((entry) => {
      if (entry.id !== entryId) return entry;
      const merged = { ...entry, ...patch, updatedAt: nowIso() };
      // Prosjektkoblingen normaliseres her, slik at «ikke pa prosjekt» faktisk
      // fjerner feltene i stedet for aa bli liggende som tom streng.
      if ('projectId' in patch) {
        ['projectId', 'projectBillable', 'projectInvoiceStatus', 'projectInvoiceId', 'projectInvoicedAt'].forEach((field) => { delete merged[field]; });
        Object.assign(merged, window.PlummeProjectCostLink?.normalizeProjectCostFields({ ...entry, ...patch }) || {});
      }
      updated = window.PlummeReceiptWorkflowSnapshot.moveReceiptWorkflowSnapshotToEntry(stripUndefinedDeep(merged));
      return updated;
    });
    return { ...state, receiptInbox };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return updated ? mapReceiptToUi(updated) : null;
};
const isReceiptStateConflict = (error) => /Foretaksdata ble endret i en annen fane/i.test(error?.message || '');
const patchAnalyzedReceiptInboxEntry = (entryId, patch) => window.PlummeStateContent.runWithStateRevisionRetry({
  fingerprint:()=>JSON.stringify({app:runtime.persistedAppStateRevision||'',shards:runtime.persistedShardRevisions||{}}),
  operation:()=>patchReceiptInboxEntry(entryId,patch),isConflict:isReceiptStateConflict,
});
const moveReceiptInboxEntriesToTrash = async (entryIds = []) => {
  const { ids, undocumentedLines } = await releaseBankLinesForTrashedReceipts(entryIds);
  const trashedAt = nowIso();
  const purgeAfter = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  await updateStateAndPersist((state) => ({
    ...state,
    receiptInbox: safeArray(state.receiptInbox).map((entry) =>
      ids.has(entry.id) ? stripUndefinedDeep({ ...entry, status: 'trashed', trashedAt, purgeAfter, updatedAt: trashedAt }) : entry,
    ),
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return { undocumentedLines };
};
const restoreReceiptInboxEntries = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  await updateStateAndPersist((state) => ({
    ...state,
    receiptInbox: safeArray(state.receiptInbox).map((entry) =>
      ids.has(entry.id)
        ? stripUndefinedDeep({ ...entry, status: entry.linkedEntryId ? 'booked' : 'pending', trashedAt: undefined, purgeAfter: undefined, updatedAt: nowIso() })
        : entry,
    ),
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
};
const deleteReceiptInboxEntriesNow = async (entryIds = []) => {
  await updateStateAndPersist((state) => deleteReceiptInboxEntriesFromState(state, entryIds));
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
};
const receiptToDocumentationEntry = (entry) => {
  const at = nowIso();
  const attachments = safeArray(entry.attachments).length
    ? safeArray(entry.attachments)
    : [stripUndefinedDeep({
        id: entry.attachmentId || `att-${entry.id}`,
        name: entry.attachmentName || entry.name || 'Bilag',
        mimeType: entry.attachmentMimeType || '',
        storagePath: entry.storagePath || '',
        downloadUrl: entry.downloadUrl || entry.file || '',
      })].filter((attachment) => attachment.downloadUrl || attachment.storagePath);
  return stripUndefinedDeep({
    id: `doc-${entry.id}`,
    name: entry.attachmentName || entry.name || 'Bilag',
    attachmentName: entry.attachmentName || entry.name || 'Bilag',
    attachmentMimeType: entry.attachmentMimeType || attachments[0]?.mimeType || '',
    file: entry.downloadUrl || entry.file || attachments[0]?.downloadUrl || undefined,
    attachments,
    source: 'receiptInbox',
    sourceReceiptInboxEntryId: entry.id,
    status: 'active',
    createdAt: entry.createdAt || at,
    updatedAt: at,
    subject: entry.subject || entry.receiptAnalysis?.supplierName || entry.attachmentName || entry.name || 'Flyttet fra innboks',
    doctype: entry.receiptAnalysis?.documentType || 'Bilag',
  });
};
const moveReceiptInboxEntriesToDocumentation = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  let movedCount = 0;
  await updateStateAndPersist((state) => {
    const receiptInbox = safeArray(state.receiptInbox);
    const moving = receiptInbox.filter((entry) => ids.has(entry.id));
    movedCount = moving.length;
    if (!moving.length) return state;
    const existingDocs = safeArray(state.documentationInbox);
    const existingDocIds = new Set(existingDocs.map((entry) => entry.id));
    const docs = moving
      .map(receiptToDocumentationEntry)
      .map((entry) => existingDocIds.has(entry.id) ? { ...entry, id: `${entry.id}-${Date.now()}` } : entry);
    return {
      ...state,
      receiptInbox: receiptInbox.filter((entry) => !ids.has(entry.id)),
      documentationInbox: [...docs, ...existingDocs],
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return movedCount;
};

// `markPending` skrives bare naar ett bilag analyseres alene. Hver patch skriver
// hele tilstandsdokumentet, og en bunke paa nitti bilag ble til hundre og aatti
// skrivinger — nok til at Firestore-koen gikk full. I en bunke er framdriften
// synlig paa knappen, og et bilag som ikke rakk turen sin blir staaende som
// «Venter paa analyse» i stedet for aa henge igjen som «Analyseres».
const analyzeReceiptInboxEntry = async (entryId, { markPending = true, forceReanalysis = false } = {}) => {
  const investmentScope = { businessId: runtime.activeBusinessId, year: Number(runtime.appState?.activeYear || new Date().getFullYear()) };
  const entry = receiptInboxBackendEntries().find((candidate) => candidate.id === entryId);
  if (!entry) throw new Error(t('svc.voucherNotFoundInInbox'));
  const previousAnalysis = entry.receiptAnalysis && typeof entry.receiptAnalysis === 'object'
    ? entry.receiptAnalysis
    : null;
  const reanalysisRequestedAt = forceReanalysis ? nowIso() : '';
  const reanalysisHistory = forceReanalysis && previousAnalysis
    ? [...safeArray(entry.receiptAnalysisHistory), stripUndefinedDeep({
        replacedAt: reanalysisRequestedAt,
        reason: 'user_requested_reanalysis',
        status: previousAnalysis.status,
        analyzedAt: previousAnalysis.analyzedAt,
        analysisSource: previousAnalysis.analysisSource,
        model: previousAnalysis.model,
        fileHash: previousAnalysis.fileHash,
        supplierName: previousAnalysis.supplierName,
        invoiceNumber: previousAnalysis.invoiceNumber,
        receiptDate: previousAnalysis.receiptDate,
        totalGross: previousAnalysis.totalGross,
        documentNetAmount: previousAnalysis.documentNetAmount,
        documentVatAmount: previousAnalysis.documentVatAmount,
        roundingAmount: previousAnalysis.roundingAmount,
        suggestedCostAccountCode: previousAnalysis.suggestedCostAccountCode,
      })].slice(-10)
    : safeArray(entry.receiptAnalysisHistory);
  if (markPending) {
    await patchAnalyzedReceiptInboxEntry(entryId, {
      ...receiptWorkflowPatch(entry, 'extracting'),
      ...(forceReanalysis ? {
        receiptAnalysisHistory: reanalysisHistory,
        workflowAuditTrail: [
          ...safeArray(entry.workflowAuditTrail),
          { stepId: 'extract_document_data', action: 'reanalysis_requested', status: 'pending', at: reanalysisRequestedAt, source: 'plumme', bankLinkPreserved: entry.status === 'linked-bank-line' || entry.bookedAs === 'bank-line' },
        ],
      } : {}),
      receiptAnalysis: { ...(entry.receiptAnalysis || {}), status: 'pending', analyzedAt: nowIso(), error: undefined },
    });
  }
  try {
    const settings = mergeState(runtime.appState).settings || {};
    const result = await authorizedPost('analyzeReceiptInboxEntryHttp', {
      businessId: runtime.activeBusinessId,
      receiptInboxEntryId: entry.id,
      attachmentId: entry.attachmentId,
      attachmentName: entry.attachmentName,
      attachmentMimeType: entry.attachmentMimeType,
      country: settings.country || 'NO',
      locale: uiLocale(),
      vatRegistered: settings.vatRegistered !== false,
      standardVat: num(settings.standardVatRate, 25),
      vehicleCostPolicy: settings.vehicleCostPolicy || undefined,
      companyTypeCode: settings.companyTypeCode || settings.companyType || '',
      forceReanalysis,
    });
    const analysis = window.PlummeReceiptCurrency.mergeReanalysis((receiptInboxBackendEntries().find((candidate) => candidate.id === entryId) || entry).receiptAnalysis, result.analysis);
    if (!analysis || analysis.status !== 'completed') throw new Error(result.message || 'Bilagsanalysen feilet.');
    if (analysis.investmentDocument) return await window.PlummeInvestments.routeInbox({ entry, analysis, ...investmentScope, patch: patchReceiptInboxEntry });
    const resolvedAnalysis = applyReceiptPaymentSourceDecision(
      analysis,
      receiptPaymentSourceDecisionForEntry(entry),
    );
    const linkedBankLine = linkedBankLineForReceiptInboxEntry(entry);
    const bankResolvedAnalysis = linkedBankLine
      ? bankLinkedReceiptAnalysisPatch(runtime.appState, linkedBankLine.statement, linkedBankLine.line, { ...entry, receiptAnalysis: resolvedAnalysis }) || resolvedAnalysis
      : resolvedAnalysis;
    const saved = await patchAnalyzedReceiptInboxEntry(entryId, {
      receiptAnalysis: bankResolvedAnalysis,
      ...(forceReanalysis ? {
        receiptAnalysisHistory: reanalysisHistory,
        workflowAuditTrail: [
          ...safeArray((receiptInboxBackendEntries().find((candidate) => candidate.id === entryId) || entry).workflowAuditTrail),
          { stepId: 'extract_document_data', action: 'reanalysis_completed', status: 'completed', at: nowIso(), source: bankResolvedAnalysis.analysisSource || 'plumme', bankLinkPreserved: Boolean(linkedBankLine) },
        ],
      } : {}),
      ...(bankResolvedAnalysis.receiptWorkflowStatus ? { receiptWorkflowStatus: bankResolvedAnalysis.receiptWorkflowStatus } : {}),
      ...(bankResolvedAnalysis.workflowSteps ? { workflowSteps: bankResolvedAnalysis.workflowSteps } : {}),
      ...(typeof bankResolvedAnalysis.confidenceScore === 'number' ? { confidenceScore: bankResolvedAnalysis.confidenceScore } : {}),
      ...(bankResolvedAnalysis.riskFlags ? { riskFlags: bankResolvedAnalysis.riskFlags } : {}),
      ...(bankResolvedAnalysis.approvalRoute ? { approvalRoute: bankResolvedAnalysis.approvalRoute } : {}),
      ...(bankResolvedAnalysis.workflowAuditTrail ? { workflowAuditTrail: bankResolvedAnalysis.workflowAuditTrail } : {}),
    });
    if (entry.source === 'editor-upload') return saved;
    await refreshBankSuggestionRules().catch((matchError) => console.warn('Kunne ikke oppdatere bankforslagene etter bilagsanalyse.', matchError));
    if (autopilotAgentEnabled('bilag')) {
      await autoMatchBankReceipts().catch((matchError) => console.warn('Automatisk bilagsmatch feilet.', matchError));
      await refreshBankReceiptMatches().catch((matchError) => console.warn('Kunne ikke oppdatere bilagstreffene.', matchError));
    }
    return saved;
  } catch (error) {
    if (runtime.activeBusinessId !== investmentScope.businessId) throw error;
    if (isReceiptStateConflict(error)) throw error;
    const reanalysisLimited = forceReanalysis && error?.status === 429 && String(error?.domainCode || '').startsWith('receipt.reanalysis');
    if (reanalysisLimited && previousAnalysis) {
      await patchAnalyzedReceiptInboxEntry(entryId, {
        receiptAnalysis: previousAnalysis,
        receiptWorkflowStatus: entry.receiptWorkflowStatus,
        approvalRoute: entry.approvalRoute,
        workflowSteps: entry.workflowSteps,
        riskFlags: entry.riskFlags,
        confidenceScore: entry.confidenceScore,
        workflowAuditTrail: [
          ...safeArray(entry.workflowAuditTrail),
          { stepId: 'extract_document_data', action: 'reanalysis_blocked', status: 'completed', at: nowIso(), source: 'plumme', message: error.message },
        ],
      });
      const failure = new Error(error.message);
      failure.status = error.status;
      failure.rateLimited = true;
      throw failure;
    }
    // Distinguish quota exhaustion from a temporary provider failure.
    // AI-tjenesten er travel akkurat naa (da hjelper det aa prove igjen).
    const quotaExhausted = error?.status === 429 && /usage limit/i.test(error?.message || '');
    // Tom leverandorkonto er ikke det samme som travel tjeneste: den gaar ikke
    // over av seg selv, og «prov igjen om litt» er feil raad.
    const providerUnavailable = /no remaining credit|provider account/i.test(error?.message || '');
    const message = /receipt attachment was not found in storage|attachment was not found in storage/i.test(error?.message || '')
      ? 'Vedlegget ble ikke funnet i lagringen. Prøv å laste opp bilaget på nytt.'
      : providerUnavailable
        ? 'Bilagsanalysen er utilgjengelig akkurat nå: AI-tjenesten mangler dekning. Bilagene ligger trygt i innboksen og kan analyseres når tjenesten er tilbake.'
        : quotaExhausted
          ? 'Månedskvoten for bilagsanalyse er brukt opp. Analysen kan kjøres igjen når kvoten er hevet eller måneden er ny.'
          : error?.status === 429
            ? 'AI-tjenesten er opptatt akkurat nå. Prøv analysen igjen om litt.'
            : error?.message || 'Bilagsanalysen feilet.';
    const failedAnalysis = applyReceiptPaymentSourceDecision({
      status: 'failed',
      analyzedAt: nowIso(),
      error: message,
    }, receiptPaymentSourceDecisionForEntry(entry));
    await patchAnalyzedReceiptInboxEntry(entryId, {
      ...receiptWorkflowPatch(entry, 'failed', message),
      receiptAnalysis: failedAnalysis,
    });
    const failure = new Error(message);
    failure.status = error?.status;
    failure.quotaExhausted = quotaExhausted;
    failure.rateLimited = error?.status === 429;
    failure.providerUnavailable = providerUnavailable;
    throw failure;
  }
};

// Bokforing skjer alltid i NOK. `PlummeReceiptCurrency.grossNok` gir null for
// bilag i fremmed valuta som ikke er omregnet, slik at bilaget hoppes over i
// stedet for aa bli bokfort med et utenlandsk tall som om det var kroner.
const receiptAnalysisBookableGrossNok = (analysis, linkedBankLine = null) => {
  const bankGross = Math.abs(num(linkedBankLine?.bankTrace?.lineAmount ?? linkedBankLine?.lineAmount));
  if (analysis?.documentType === 'settlement_report') {
    return num(analysis?.settlementGrossAmount || analysis?.totalGross) || null;
  }
  if (bankGross > 0.005 && String(analysis?.currency || 'NOK').toUpperCase() === 'NOK') return bankGross;
  return window.PlummeReceiptCurrency?.grossNok?.(analysis) ?? num(analysis?.totalGross);
};
const receiptAnalysisBookableNetAmount = (analysis, linkedBankLine = null) => {
  const gross = receiptAnalysisBookableGrossNok(analysis, linkedBankLine);
  if (gross == null) return null;
  const documentNet = Math.round(num(analysis.documentNetAmount) * 100) / 100;
  const documentVat = Math.round(num(analysis.documentVatAmount ?? analysis.vatAmount) * 100) / 100;
  const roundingAmount = Math.round(num(analysis.roundingAmount) * 100) / 100;
  if (documentNet > 0 && documentVat >= 0 && Math.abs(Math.round((documentNet + documentVat + roundingAmount) * 100) / 100 - gross) <= 0.02) return documentNet;
  const rate = num(analysis.vatRate);
  const vatFactor = window.PlummePurchaseVatTreatment?.grossFactor({ vatRate: rate, reverseChargeCode: analysis.reverseChargeCode, vatTreatment: analysis.vatTreatment })
    ?? (analysis.reverseChargeCode === '86' ? 1 : 1 + Math.max(0, rate) / 100);
  return Math.round((gross / vatFactor) * 100) / 100;
};

// ---------------------------------------------------------------------------
// Forhandssjekk for bokforing fra innboksen
// ---------------------------------------------------------------------------
// Bulk-bokforing skjer i blinde: brukeren har krysset av 24 rader og ser ingen
// av dokumentene. Da maa duplikatene stoppes for noe skrives, og brukeren maa
// etterpaa faa vite hva som ble hoppet over. Enkeltbokforing er en annen
// situasjon: der ligger bilaget aapent foran brukeren, og advarselen i panelet
// er nok.
//
// Sperren folger derfor hvor sikkert signalet er:
//   'block'        (bulk)      samme fil og samme fakturanummer sperrer,
//                              innholdstreff hoppes over
//   'allowContent' (bekreftet) brukeren har huket av for aa ta med
//                              innholdstreffene likevel
//   'allow'        (ett bilag) bare fakturanummeret sperrer - det er en
//                              bokforingsfeil, ikke en vurdering
const receiptBookingEntryName = (entry) => entry?.name || entry?.attachmentName || 'Bilag';

const receiptBookingPlanCounts = (book, skipped) => {
  const counts = { book: book.length, skipped: skipped.length };
  skipped.forEach((item) => { counts[item.kind] = (counts[item.kind] || 0) + 1; });
  // Bilag som er klare, men der noe er forvalgt av oss — betalingskilde fra
  // historikk, standardvalg eller banken. De skal bokfores, men brukeren skal se
  // hvor mange det gjelder for hen trykker.
  counts.withNotes = book.filter((item) => safeArray(item.notes).length > 0).length;
  return counts;
};

const buildReceiptBookingPlan = (entries, entryIds, { duplicates = 'block' } = {}, deps = {}) => {
  const invoiceNumberRegistered = deps.invoiceNumberRegistered || (() => false);
  const expenseDuplicatesFor = deps.expenseDuplicatesFor || (() => []);
  const byId = new Map(safeArray(entries).map((entry) => [entry.id, entry]));
  // Samme fakturanummer to ganger i samme utvalg fanges ikke av sjekken mot
  // lagrede utgifter: den forste er ikke bokfort enda naar den andre vurderes.
  const plannedInvoiceNumbers = new Set();
  const book = [];
  const skipped = [];
  safeArray(entryIds).forEach((id) => {
    const entry = byId.get(id);
    const name = entry ? receiptBookingEntryName(entry) : String(id);
    const skip = (kind, reason) => skipped.push({ id, name, kind, reason });
    if (!entry) { skip('missing', t('svc.bookSkipMissing')); return; }
    if (entry.review?.status !== 'ready-to-book') {
      skip('needs-review', safeArray(entry.review?.reasons).join(' ') || t('svc.bookSkipNeedsReview'));
      return;
    }
    const analysis = entry.receiptAnalysis || {};
    const invoiceNumber = analysis.documentType === 'supplier_invoice' ? String(analysis.invoiceNumber || '').trim() : '';
    const invoiceKey = invoiceNumber.toLocaleLowerCase('nb-NO');
    if (invoiceKey && (plannedInvoiceNumbers.has(invoiceKey) || invoiceNumberRegistered(invoiceNumber))) {
      skip('duplicate-invoice-number', t('svc.bookSkipSameInvoiceNumber', { number: invoiceNumber }));
      return;
    }
    if (duplicates !== 'allow') {
      if (entry.duplicateUpload?.reason === 'file') { skip('duplicate-file', t('svc.bookSkipSameFile')); return; }
      if (duplicates === 'block' && (entry.duplicateUpload?.reason === 'content' || expenseDuplicatesFor(entry).length > 0)) {
        skip('duplicate-content', t('svc.bookSkipSameContent'));
        return;
      }
    }
    if (invoiceKey) plannedInvoiceNumbers.add(invoiceKey);
    book.push({ id, name, notes: safeArray(entry.review?.notes) });
  });
  return { book, skipped, counts: receiptBookingPlanCounts(book, skipped) };
};

const getReceiptInboxBookingPlan = (entryIds = [], options = {}) => buildReceiptBookingPlan(
  getReceiptInboxModel().entries,
  entryIds,
  options,
  {
    invoiceNumberRegistered: (number) => safeArray(runtime.appState?.expenses).some((expense) =>
      expense.isSupplierInvoice &&
      !expense.annulledAt &&
      String(expense.supplierInvoiceNumber || '').trim().toLocaleLowerCase('nb-NO') ===
        String(number).trim().toLocaleLowerCase('nb-NO'),
    ),
    expenseDuplicatesFor: (entry) => {
      const analysis = entry.receiptAnalysis || {};
      const gross = window.PlummeReceiptCurrency.grossNok(analysis);
      if (gross == null) return [];
      const matches = findPossibleDuplicateExpenses({
        date: analysis.receiptDate,
        grossAmount: gross,
        supplierName: analysis.supplierName,
      });
      // Star det et leverandornavn paa bilaget, teller bare treff hos samme
      // leverandor. To like belop samme dag hos to butikker er ikke et duplikat.
      return normalizedPartyName(analysis.supplierName) ? matches.filter((match) => match.sameSupplier) : matches;
    },
  },
);

const receiptPaymentSourceForAccountCode = (accountCode) => {
  const code = String(accountCode || '').trim();
  if (!code) return 'unknown';
  if (code === 'private' || /^206\d/.test(code)) return 'private';
  if (code === '1900') return 'cash';
  return 'company';
};

const normalizeReceiptPaymentSourceDecision = (decision = {}) => {
  const source = decision && typeof decision === 'object' ? decision : {};
  const kind = String(source.kind || '').trim();
  if (!['account', 'unpaid', 'analyze'].includes(kind)) return null;
  const accountCode = String(source.accountCode || '').trim();
  if (kind === 'account' && !accountCode) return null;
  return stripUndefinedDeep({
    kind,
    accountCode: kind === 'account' ? accountCode : undefined,
    origin: String(source.origin || 'manual-at-upload').trim() || 'manual-at-upload',
    decidedAt: source.decidedAt || nowIso(),
  });
};

// Betalingsvalget er brukerens opplysning, mens OCR-resultatet er et forslag.
// Beslutningen ligger derfor utenfor analysen og legges tilbake etter hver
// analyse. Et konkret kort-/bankfunn som peker til en annen konto beholdes som
// kontrollgrunn, men overskriver aldri brukerens valg lydlost.
const applyReceiptPaymentSourceDecision = (analysis = {}, decisionInput = {}) => {
  const decision = normalizeReceiptPaymentSourceDecision(decisionInput);
  if (!decision || decision.kind === 'analyze') return { ...analysis };
  const detectedAccountCode = String(analysis.paymentAccountCode || '').trim();
  const detectedOrigin = String(analysis.paymentSourceOrigin || '').trim();
  const detectedIsHardEvidence = detectedAccountCode
    && ['payment-card', 'bank-match'].includes(detectedOrigin)
    && analysis.paymentEvidence?.requiresReview !== true
    && (analysis.paymentEvidence?.confidence === 'high' || detectedOrigin === 'payment-card');
  if (decision.origin === 'manual-at-upload' && detectedIsHardEvidence) {
    return { ...analysis };
  }
  const next = { ...analysis };
  const flags = safeArray(next.riskFlags).filter((flag) => (
    flag?.code !== 'missing_payment_source'
    && flag?.code !== 'payment_evidence_review'
    && flag?.code !== 'payment_source_conflict'
  ));
  if (decision.kind === 'unpaid') {
    next.isPaid = false;
    next.paymentStatusOrigin = decision.origin;
    delete next.paymentAccountCode;
    delete next.paymentSourceOrigin;
    if (next.documentType === 'supplier_invoice') next.riskFlags = flags;
    return next;
  }
  const detectedIsEvidence = detectedAccountCode
    && detectedAccountCode !== decision.accountCode
    && ['payment-card', 'bank-match'].includes(detectedOrigin);
  next.paymentAccountCode = decision.accountCode;
  next.isPaid = true;
  next.paymentStatusOrigin = decision.origin;
  next.suggestedPaymentSource = receiptPaymentSourceForAccountCode(decision.accountCode);
  next.paymentSourceOrigin = decision.origin;
  next.riskFlags = detectedIsEvidence
    ? [...flags, {
        code: 'payment_source_conflict',
        severity: 'review',
        message: `Du valgte konto ${decision.accountCode}, men bilaget peker mot konto ${detectedAccountCode}. Kontroller betalingskilden.`,
        stepId: 'check_anomalies',
      }]
    : flags;
  if (detectedIsEvidence) {
    next.paymentSourceConflict = {
      selectedAccountCode: decision.accountCode,
      detectedAccountCode,
      detectedOrigin,
    };
    next.approvalRoute = 'manual_review';
    next.workflowSteps = safeArray(next.workflowSteps).map((step) => step?.id === 'check_anomalies'
      ? { ...step, status: 'review', riskFlags: [...new Set([...safeArray(step.riskFlags), 'payment_source_conflict'])] }
      : step);
  } else {
    delete next.paymentSourceConflict;
  }
  return next;
};

const receiptPaymentSourceDecisionForEntry = (entry = {}) => {
  const stored = normalizeReceiptPaymentSourceDecision(entry.paymentSourceDecision);
  if (stored) return stored;
  const analysis = entry.receiptAnalysis || {};
  if (!['manual', 'manual-at-upload'].includes(analysis.paymentSourceOrigin)) return null;
  return normalizeReceiptPaymentSourceDecision({
    kind: 'account',
    accountCode: analysis.paymentAccountCode,
    origin: analysis.paymentSourceOrigin,
    decidedAt: entry.updatedAt || entry.createdAt,
  });
};

// Betalingskilde for mange bilag samtidig. Kontokoden lagres paa analysen, slik
// at bade forslaget, kontrollen og bokforingen ser det samme valget.
const setReceiptInboxPaymentSource = async (entryIds = [], accountCode = '') => {
  const ids = new Set(safeArray(entryIds));
  const code = String(accountCode || '').trim();
  if (!ids.size) return 0;
  const entries = receiptInboxBackendEntries().filter((entry) => ids.has(entry.id) && entry.receiptAnalysis);
  for (const entry of entries) {
    const paymentSourceDecision = code ? normalizeReceiptPaymentSourceDecision({
      kind: 'account',
      accountCode: code,
      origin: 'manual',
    }) : null;
    await patchReceiptInboxEntry(entry.id, {
      paymentSourceDecision,
      receiptAnalysis: code
        ? applyReceiptPaymentSourceDecision(entry.receiptAnalysis, paymentSourceDecision)
        : {
            ...entry.receiptAnalysis,
            paymentAccountCode: null,
            suggestedPaymentSource: 'unknown',
            paymentSourceOrigin: null,
          },
    });
  }
  return entries.length;
};

// «Oppdater betalingskilde fra bankutskriften»: skriver den avledede
// bankslutningen inn paa bilagene, slik at den blir en verdi noen har godkjent i
// stedet for et forslag som regnes ut paa nytt hver gang. Bare banksporet
// skrives — historikk og standardvalg forblir forslag til brukeren tar dem selv.
const receiptInboxBankPaymentSourceCandidates = () => receiptInboxBackendEntries()
  .filter((entry) => entry.source !== 'editor-upload')
  .filter((entry) => !receiptInboxEntryIsTrashed(entry) && entry.status !== 'booked')
  .filter((entry) => entry.receiptAnalysis?.status === 'completed')
  .filter((entry) => !entry.receiptAnalysis.paymentAccountCode)
  .map((entry) => ({ entry, hint: window.PlummeReceiptPaymentSource.suggest(runtime.appState, entry.receiptAnalysis) }))
  .filter(({ hint }) => hint && (hint.origin === 'bank-match' || hint.origin === 'bank-absence'))
  .map(({ entry, hint }) => ({
    id: entry.id,
    name: entry.attachmentName || entry.receiptAnalysis.supplierName || 'Bilag',
    supplierName: entry.receiptAnalysis.supplierName || '',
    date: entry.receiptAnalysis.receiptDate || '',
    amount: num(entry.receiptAnalysis.totalGrossNok ?? entry.receiptAnalysis.totalGross),
    paymentSource: hint.paymentSource,
    accountCode: hint.accountCode || (hint.paymentSource === 'cash' ? '1900' : ''),
    origin: hint.origin,
    confidence: hint.confidence,
    requiresReview: hint.requiresReview === true || hint.confidence !== 'high',
    reason: hint.reason,
  }));

const applyBankPaymentSourceHints = async (entryIds = []) => {
  const wanted = new Set(safeArray(entryIds));
  const candidates = receiptInboxBankPaymentSourceCandidates()
    .filter((candidate) => !wanted.size || wanted.has(candidate.id));
  for (const candidate of candidates) {
    const entry = receiptInboxBackendEntries().find((item) => item.id === candidate.id);
    if (!entry?.receiptAnalysis) continue;
    await patchReceiptInboxEntry(entry.id, {
      receiptAnalysis: {
        ...entry.receiptAnalysis,
        paymentAccountCode: candidate.accountCode,
        suggestedPaymentSource: candidate.paymentSource,
        paymentSourceOrigin: candidate.origin,
        paymentEvidence: {
          origin: candidate.origin,
          confidence: candidate.confidence,
          paymentSource: candidate.paymentSource,
          accountCode: candidate.accountCode || undefined,
          requiresReview: candidate.requiresReview,
          reason: candidate.reason,
        },
        ...(candidate.requiresReview ? {
          approvalRoute: 'manual_review',
          receiptWorkflowStatus: 'manual_review',
          riskFlags: [
            ...safeArray(entry.receiptAnalysis.riskFlags).filter((flag) => flag?.code !== 'payment_evidence_review'),
            { code: 'payment_evidence_review', severity: 'review', message: 'Betalingskilden må kontrolleres.', stepId: 'check_anomalies' },
          ],
        } : {}),
      },
    });
  }
  return candidates.length;
};

const linkedBankLineForReceiptInboxEntry = (entry = {}) => {
  const receiptId = String(entry.id || '').trim();
  if (!receiptId) return null;
  const state = mergeState(runtime.appState);
  const linked = bankLinkedReceiptIndex(state).get(receiptId);
  if (!linked?.statementId || !linked?.lineId) return null;
  const statement = safeArray(state.bankStatements).find((item) => String(item?.id || '') === String(linked.statementId));
  const line = safeArray(statement?.lines).find((item) => String(item?.id || '') === String(linked.lineId));
  if (!statement || !line) return null;
  return {
    ...linked,
    statement,
    line,
    bankTrace: {
      statementId: statement.id,
      lineId: line.id,
      lineDate: line.date,
      lineDescription: line.description,
      lineAmount: num(line.amount),
    },
  };
};

const receiptSettlementExpectedPayoutAmount = (analysis = {}) => {
  const explicit = analysis.settlementPayoutAmount;
  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== '') return num(explicit);
  const gross = num(analysis.settlementGrossAmount || analysis.totalGross);
  const fee = Math.max(0, num(analysis.settlementFeeAmount));
  return Math.max(0, gross - fee);
};

const receiptSettlementAddDays = (date, days) => {
  const parsed = new Date(`${String(date || '').slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

const receiptSettlementDescriptionHasId = (description, settlementId) => {
  const id = String(settlementId || '').replace(/\D/g, '');
  if (!id) return false;
  const digits = String(description || '').replace(/\D+/g, ' ').trim().split(/\s+/);
  return digits.includes(id);
};

const receiptSettlementBankLineCandidates = ({ entry = {}, analysis = {}, accountCode = '', state = runtime.appState } = {}) => {
  if (analysis?.documentType !== 'settlement_report') return [];
  const payout = receiptSettlementExpectedPayoutAmount(analysis);
  const receiptId = String(entry.id || '').trim();
  const settlementId = String(analysis.settlementId || '').trim();
  const settlementDate = String(analysis.receiptDate || '').slice(0, 10);
  const lastDate = settlementDate ? receiptSettlementAddDays(settlementDate, 7) : '';
  const wantedAccountCode = String(accountCode || analysis.paymentAccountCode || '').trim();
  if (!(payout > 0) || !settlementId || !settlementDate) return [];
  return safeArray(state?.bankStatements).flatMap((statement) => {
    const statementAccountCode = String(bankStatementLedgerAccountCode(state, statement, '') || '').trim();
    if (wantedAccountCode && statementAccountCode && statementAccountCode !== wantedAccountCode) return [];
    return safeArray(statement?.lines).map((line) => ({
      statement,
      line,
      statementId: statement.id,
      lineId: line?.id,
      accountCode: statementAccountCode,
      bankAccountId: statement.bankAccountId || '',
      lineDescription: line?.description || '',
      lineAmount: num(line?.amount),
      suggested: true,
      booked: Boolean(line?.matchedEntryId),
      bankTrace: {
        statementId: statement.id,
        lineId: line?.id,
        lineDate: line?.date,
        lineDescription: line?.description,
        lineAmount: num(line?.amount),
      },
    }));
  }).filter((candidate) => {
    const line = candidate.line || {};
    if (!candidate.statementId || !candidate.lineId) return false;
    if (candidate.booked) return false;
    const linkedReceiptId = String(line.receiptInboxEntryId || line.aiSuggestedReceiptInboxEntryId || '').trim();
    if (linkedReceiptId && linkedReceiptId !== receiptId) return false;
    if (Math.abs(num(line.amount) - payout) > 0.01) return false;
    const lineDate = String(line.date || '').slice(0, 10);
    if (!lineDate || lineDate < settlementDate || (lastDate && lineDate > lastDate)) return false;
    return receiptSettlementDescriptionHasId(line.description, settlementId);
  });
};

const receiptSettlementBankLineForInboxEntry = ({ entry = {}, analysis = {}, accountCode = '', state = runtime.appState } = {}) => {
  const linked = linkedBankLineForReceiptInboxEntry(entry);
  if (linked) return linked;
  const candidates = receiptSettlementBankLineCandidates({ entry, analysis, accountCode, state });
  return candidates.length === 1 ? candidates[0] : null;
};

const markLinkedBankLineBookedFromInbox = async ({ linked, entry, entryType, entryId, bookingType, accountCode, vatRate }) => {
  if (!linked?.statementId || !linked?.lineId || !entryId) return;
  const existingMatch = String(linked.line?.matchedEntryId || '').trim();
  if (existingMatch && existingMatch !== String(entryId)) return;
  await updateBankStatementLine(linked.statementId, linked.lineId, stripUndefinedDeep({
    bookingType,
    classification: 'business',
    reconciliationStatus: 'matched',
    matchedEntryType: entryType,
    matchedEntryId: entryId,
    receiptInboxEntryId: entry?.id,
    attachmentName: entry?.attachmentName || entry?.name,
    attachmentId: entry?.attachmentId,
    attachmentMimeType: entry?.attachmentMimeType,
    attachmentDownloadUrl: entry?.downloadUrl || entry?.file,
    accountCode,
    vatRate,
    bankTrace: linked.bankTrace,
    note: 'Bokført fra innboks og koblet til banklinjen.',
  }));
};

const receiptSettlementInvoiceGrossAmount = (invoice) => {
  const lineGross = Math.abs(num(invoiceGross(invoice)));
  if (lineGross > 0.005) return lineGross;
  return Math.abs(num(invoice?.grossAmount ?? invoice?.totalAmount ?? invoice?.amount));
};

const receiptSettlementOpenInvoiceMatches = (analysis, { state = runtime.appState, selectedInvoiceId = '' } = {}) => {
  const gross = num(analysis?.settlementGrossAmount || analysis?.totalGross);
  if (!(gross > 0)) return [];
  const selectedId = String(selectedInvoiceId || analysis?.settlementInvoiceId || '').trim();
  const payments = safeArray(state?.payments).filter((payment) => payment?.status !== 'reversed');
  return safeArray(state?.invoices)
    .filter((invoice) => invoice?.status !== 'draft' && invoice?.kind !== 'credit' && invoice?.kind !== 'offer' && invoice?.kind !== 'order_confirmation')
    .map((invoice) => {
      const paid = payments
        .filter((payment) => payment.documentType === 'invoice' && payment.documentId === invoice.id)
        .reduce((sum, payment) => sum + num(payment.amount), 0);
      const total = receiptSettlementInvoiceGrossAmount(invoice);
      const remaining = invoice.remainingAmount == null || String(invoice.remainingAmount).trim() === ''
        ? Math.max(0, total - paid)
        : num(invoice.remainingAmount);
      const date = String(invoice.date || invoice.createdAt || '').slice(0, 10);
      const dueDate = String(invoice.dueDate || invoice.validUntil || '').slice(0, 10);
      const settlementDate = String(analysis?.receiptDate || '').slice(0, 10);
      const selected = Boolean(selectedId && String(invoice.id) === selectedId);
      const score = (selected ? 100 : 0) + 50 + (settlementDate && date && date <= settlementDate ? 5 : 0) + (settlementDate && dueDate && dueDate >= settlementDate ? 2 : 0);
      return {
        invoice,
        remaining,
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber || invoice.number || invoice.documentNumber || invoice.id,
        customerName: invoice.customer?.name || invoice.customerName || '',
        date,
        dueDate,
        totalAmount: backendRoundMoney(total),
        remainingAmount: backendRoundMoney(remaining),
        matchedAmount: backendRoundMoney(gross),
        score,
        selected,
        matchReason: 'Samme beløp som gjenstår på åpen kundefaktura.',
      };
    })
    .filter(({ invoice, remaining }) => invoice?.id && remaining > 0.005 && Math.abs(remaining - gross) <= 0.01);
};

const receiptSettlementOpenInvoiceMatchSuggestions = (analysis, options = {}) =>
  receiptSettlementOpenInvoiceMatches(analysis, options)
    .sort((left, right) => right.score - left.score || String(right.date).localeCompare(String(left.date)))
    .slice(0, 5);

const bookOneReceiptInboxEntry = async (entry, selectedPaymentAccount) => {
  const skip = (kind, reason) => ({ status: 'skipped', kind, reason });
  const linkedBankLine = linkedBankLineForReceiptInboxEntry(entry);
  const normalizedAnalysis = receiptInboxAnalysisWithLinkedContext(entry, linkedBankLine, runtime.appState);
  const analysis = normalizedAnalysis?.status === 'completed' ? normalizedAnalysis : null;
  if (!analysis || analysis.suggestedPostingType === 'free-posting') return skip('missing-data', t('svc.bookSkipManual'));
  if (analysis.documentType === 'settlement_report') {
    const gross = num(analysis.settlementGrossAmount || analysis.totalGross);
    const fee = Math.max(0, num(analysis.settlementFeeAmount));
    const payout = receiptSettlementExpectedPayoutAmount(analysis);
    if (!(gross > 0) || !analysis.receiptDate) return skip('missing-data', t('svc.bookSkipMissingData'));
    const settlementAccountCode = String(selectedPaymentAccount || analysis.paymentAccountCode || '').trim();
    if (!settlementAccountCode) return skip('missing-data', t('svc.bookSkipNoPaymentAccount'));
    const settlementBankLine = linkedBankLine || receiptSettlementBankLineForInboxEntry({ entry, analysis, accountCode: settlementAccountCode });
    const selectedInvoiceId = String(analysis.settlementInvoiceId || '').trim();
    const invoiceMatches = receiptSettlementOpenInvoiceMatches(analysis, { selectedInvoiceId });
    const selectedMatch = selectedInvoiceId
      ? invoiceMatches.find((match) => String(match.invoiceId || match.invoice?.id || '') === selectedInvoiceId)
      : null;
    if (selectedInvoiceId && !selectedMatch) return skip('manual-allocation', 'Den valgte fakturaen er ikke lenger en apen match for oppgjoret. Velg faktura pa nytt.');
    const bookableMatch = selectedMatch || (invoiceMatches.length === 1 ? invoiceMatches[0] : null);
    const reference = [analysis.settlementProvider || 'Oppgjør', analysis.settlementId].filter(Boolean).join(' ');
    if (!bookableMatch && invoiceMatches.length > 1) return skip('manual-allocation', 'Oppgjøret matcher flere åpne fakturaer. Fordel oppgjøret manuelt før bokføring.');
    if (bookableMatch) {
      const payment = await addInvoicePayment(bookableMatch.invoice.id, {
        id: `payment-settlement-${entry.id}`,
        date: analysis.receiptDate,
        amount: gross,
        feeAmount: fee,
        feeAccountCode: '7770',
        settlementAccountCode,
        reference,
        note: `${reference || 'Oppgjør'} fra innboksen.`,
        createdByFlow: 'receipt-settlement',
        receiptInboxEntryId: entry.id,
        bankAmount: payout,
        bankTrace: settlementBankLine?.bankTrace,
      });
      await markLinkedBankLineBookedFromInbox({
        linked: settlementBankLine,
        entry,
        entryType: 'payment',
        entryId: payment?.id,
        bookingType: 'invoice-payment',
        accountCode: settlementAccountCode,
        vatRate: 0,
      });
      await patchReceiptInboxEntry(entry.id, {
        ...receiptWorkflowPatch(entry, 'booked', payment?.id),
        status: 'booked',
        bookedAs: 'invoice-payment',
        bookedAt: nowIso(),
        linkedEntryId: payment?.id,
      });
      return { status: 'booked' };
    }
    const amountNet = receiptAnalysisBookableNetAmount(analysis, null);
    const sale = buildOtherSaleFromEditor({
      docType: 'annet',
      invDate: analysis.receiptDate,
      descr: reference || entry.attachmentName.replace(/\.[^/.]+$/, ''),
      reference: entry.attachmentName,
      paidTo: settlementAccountCode,
      paymentSource: /^206\d|private/i.test(settlementAccountCode) ? 'private' : settlementAccountCode === '1900' ? 'cash' : 'company',
      lines: [{
        desc: reference || entry.attachmentName.replace(/\.[^/.]+$/, ''),
        qty: 1,
        price: amountNet,
        vat: analysis.vatRate ?? 0,
      }],
      attachments: safeArray(mapReceiptToUi(entry).attachments),
    });
    sale.receiptInboxEntryId = entry.id;
    sale.settlementProvider = analysis.settlementProvider;
    sale.settlementId = analysis.settlementId;
    sale.settlementFeeAmount = fee;
    sale.settlementFeeAccountCode = '7770';
    sale.settlementPayoutAmount = payout;
    if (settlementBankLine?.bankTrace) sale.bankTrace = settlementBankLine.bankTrace;
    const savedSale = await upsertOtherSale(sale);
    await markLinkedBankLineBookedFromInbox({
      linked: settlementBankLine,
      entry,
      entryType: 'other-sale',
      entryId: savedSale?.id || sale.id,
      bookingType: 'sale',
      accountCode: sale.lines?.[0]?.salesAccountCode || sale.salesAccountCode,
      vatRate: analysis.vatRate ?? 0,
    });
    await patchReceiptInboxEntry(entry.id, {
      ...receiptWorkflowPatch(entry, 'booked', savedSale?.id || sale.id),
      status: 'booked',
      bookedAs: 'other-sale',
      bookedAt: nowIso(),
      linkedEntryId: savedSale?.id || sale.id,
    });
    return { status: 'booked' };
  }
  if (analysis.suggestedPostingType === 'other-sale') {
    const amountNet = receiptAnalysisBookableNetAmount(analysis, linkedBankLine);
    if (amountNet == null || !analysis.receiptDate) return skip('missing-data', t('svc.bookSkipMissingData'));
    const salePayAcct = String(selectedPaymentAccount || analysis.paymentAccountCode || '').trim()
      || (analysis.suggestedPaymentSource === 'cash' ? '1900' : '');
    if (!salePayAcct) return skip('missing-data', t('svc.bookSkipNoPaymentAccount'));
    const sale = buildOtherSaleFromEditor({
      docType: 'annet',
      invDate: analysis.receiptDate,
      descr: analysis.supplierName || entry.attachmentName.replace(/\.[^/.]+$/, ''),
      reference: entry.attachmentName,
      lines: [{
        desc: analysis.supplierName || entry.attachmentName.replace(/\.[^/.]+$/, ''),
        qty: 1,
        price: amountNet,
        vat: analysis.vatRate ?? 0,
      }],
      attachments: safeArray(mapReceiptToUi(entry).attachments),
    });
    sale.paymentSource = /^206\d|private/i.test(salePayAcct) ? 'private' : salePayAcct === '1900' ? 'cash' : 'company';
    sale.paymentAccountCode = salePayAcct;
    sale.receiptInboxEntryId = entry.id;
    if (linkedBankLine?.bankTrace) sale.bankTrace = linkedBankLine.bankTrace;
    const savedSale = await upsertOtherSale(sale);
    await markLinkedBankLineBookedFromInbox({
      linked: linkedBankLine,
      entry,
      entryType: 'other-sale',
      entryId: savedSale?.id || sale.id,
      bookingType: 'sale',
      accountCode: sale.lines?.[0]?.salesAccountCode || sale.salesAccountCode,
      vatRate: analysis.vatRate ?? 0,
    });
    await patchReceiptInboxEntry(entry.id, {
      ...receiptWorkflowPatch(entry, 'booked', savedSale?.id || sale.id),
      status: 'booked',
      bookedAs: 'other-sale',
      bookedAt: nowIso(),
      linkedEntryId: savedSale?.id || sale.id,
    });
    queueAiLearningLog(() => logReceiptSuggestionOutcome({
      entry,
      outcome: 'booked',
      finalChoice: {
        receiptInboxEntryId: entry.id,
        bookedAs: 'other-sale',
        linkedEntryId: savedSale?.id || sale.id,
        userChangedFields: safeArray(analysis.userEditedFields),
        postingType: analysis.suggestedPostingType,
        vatRate: analysis.vatRate ?? 0,
        paymentSource: sale.paymentSource,
        paymentAccountCode: sale.paymentAccountCode,
      },
    }), { sourceType: 'receipt_inbox', sourceId: entry.id, outcome: 'booked' });
    return { status: 'booked' };
  }
  const isSupplierInvoice = analysis.documentType === 'supplier_invoice';
  const amountNet = receiptAnalysisBookableNetAmount(analysis, linkedBankLine);
  if (amountNet == null || !analysis.receiptDate) return skip('missing-data', t('svc.bookSkipMissingData'));
  // Uavklart betalingskilde skal aldri bli til driftskontoen. `receiptInboxReview`
  // hindrer normalt at slike bilag naar hit, men fallbacket sto igjen som en
  // stille gjetning bak den kontrollen.
  const suggestedPayAcct = analysis.suggestedPaymentSource === 'cash'
      ? '1900'
      : '';
  // Kontoen brukeren valgte i bilagsforslaget ligger paa analysen. For laa den
  // bare i sidas eget minne, og et sideskift kastet valget.
  const payAcct = selectedPaymentAccount || String(analysis.paymentAccountCode || '').trim() || suggestedPayAcct;
  // En leverandorfaktura som ikke er betalt skal bokfores uten betalingskonto:
  // belopet blir staaende som leverandorgjeld til betalingen registreres. For et
  // vanlig kjop stopper vi fortsatt — der er betalingskilden en del av bilaget.
  const unpaidSupplierInvoice = isSupplierInvoice && analysis.isPaid === false;
  if (!payAcct && !unpaidSupplierInvoice) return skip('missing-data', t('svc.bookSkipNoPaymentAccount'));
  if (analysis.vatTreatment !== 'vatOnly' && !analysis.suggestedCostAccountCode && !safeArray(analysis.purchaseLines).some((line) => line?.costAccountCode)) {
    return skip('missing-data', 'Mangler kostnadskonto.');
  }
  if (analysis.purchaseLineValidation?.status === 'mismatch') {
    return skip('missing-data', 'Bilagslinjene og MVA-beløpet må stemme med totalsummen.');
  }
  const validatedPurchaseLines = analysis.purchaseLineValidation?.status === 'valid'
    ? safeArray(analysis.purchaseLines)
    : [];
  const purchase = buildPurchaseFromEditor({
    kjopType: isSupplierInvoice ? 'Registrer faktura' : 'Vanlig kjop',
    isPaid: isSupplierInvoice
      ? (typeof analysis.isPaid === 'boolean' ? analysis.isPaid : Boolean(payAcct))
      : true,
    payAcct,
    date: analysis.receiptDate,
    payDate: analysis.receiptDate,
    // Forfallsdatoen fra bilagsforslaget nadde aldri fram til utgiften: den ble
    // lagret paa analysen og deretter glemt her. Uten frist paa dokumentet er
    // bilagsdatoen fristen.
    dueDate: isSupplierInvoice ? (analysis.dueDate || analysis.receiptDate) : undefined,
    supplierId: analysis.supplierId || '', supplierName: analysis.supplierName || '', supplierOrganizationNumber: analysis.supplierOrganizationNumber || analysis.supplierOrgNumber || '',
    invoiceNumber: isSupplierInvoice ? analysis.invoiceNumber : '',
    description: analysis.supplierName || entry.attachmentName.replace(/\.[^/.]+$/, ''),
    lines: validatedPurchaseLines.length
      ? validatedPurchaseLines.map((line) => ({
          desc: line.description || line.costAccountName || analysis.supplierName || entry.attachmentName.replace(/\.[^/.]+$/, ''),
          net: num(line.amountNet),
          vat: typeof line.vatRate === 'number' ? line.vatRate : (analysis.vatRate ?? 0),
          vatAmount: analysis.vatTreatment === 'vatOnly' ? undefined : line.vatAmount,
          vatTreatment: line.vatTreatment || analysis.vatTreatment,
          purchaseVatCode: line.purchaseVatCode || analysis.purchaseVatCode,
          standardVatCode: line.standardVatCode || analysis.standardVatCode,
          costAccountCode: analysis.vatTreatment === 'vatOnly' ? undefined : line.costAccountCode || analysis.suggestedCostAccountCode,
          reverse: line.reverseChargeCode || analysis.reverseChargeCode || undefined,
        }))
      : [{
          desc: analysis.suggestedCostAccountName || analysis.supplierName || entry.attachmentName.replace(/\.[^/.]+$/, ''),
          net: amountNet,
          vat: analysis.vatRate ?? 0,
          vatAmount: analysis.vatTreatment === 'vatOnly' ? undefined : analysis.documentVatAmount ?? analysis.vatAmount,
          vatTreatment: analysis.vatTreatment,
          purchaseVatCode: analysis.purchaseVatCode,
          standardVatCode: analysis.standardVatCode,
          costAccountCode: analysis.vatTreatment === 'vatOnly' ? undefined : analysis.suggestedCostAccountCode,
          reverse: analysis.reverseChargeCode || undefined,
        }],
    receipt: mapReceiptToUi(entry),
    currency: analysis.currency,
    originalTotalGross: analysis.totalGross,
    totalGrossNok: analysis.totalGrossNok,
    documentGrossAmount: analysis.totalGross,
    roundingAmount: analysis.roundingAmount,
    roundingAccountCode: analysis.roundingAmount ? '7740' : undefined,
    exchangeRate: analysis.exchangeRate,
    exchangeRateDate: analysis.exchangeRateDate,
    exchangeRateSource: analysis.exchangeRateSource,
  });
  purchase.receiptWorkflowStatus = 'booked';
  purchase.approvalRoute = entry.approvalRoute || analysis.approvalRoute || 'quick_approval';
  if (linkedBankLine?.bankTrace) {
    purchase.bankTrace = linkedBankLine.bankTrace;
    purchase.source = 'bank-linked-inbox';
  }
  const savedPurchase = await upsertPurchase(purchase);
  const bookedPurchaseId = savedPurchase?.id || purchase.id;
  await markLinkedBankLineBookedFromInbox({
    linked: linkedBankLine,
    entry,
    entryType: 'expense',
    entryId: bookedPurchaseId,
    bookingType: isSupplierInvoice ? 'supplier-invoice' : 'purchase',
    accountCode: purchase.costAccountCode || safeArray(purchase.purchaseLines)[0]?.costAccountCode,
    vatRate: purchase.vatRate ?? analysis.vatRate ?? 0,
  });
  await patchReceiptInboxEntry(entry.id, {
    ...receiptWorkflowPatch(entry, 'booked', bookedPurchaseId),
    status: 'booked',
    bookedAs: 'expense',
    bookedAt: nowIso(),
    linkedEntryId: bookedPurchaseId,
  });
  queueAiLearningLog(() => logReceiptSuggestionOutcome({
    entry,
    outcome: 'booked',
    finalChoice: {
      receiptInboxEntryId: entry.id,
      bookedAs: 'expense',
      linkedEntryId: bookedPurchaseId,
      userChangedFields: safeArray(analysis.userEditedFields),
      documentType: analysis.documentType,
      postingType: analysis.suggestedPostingType,
      paymentSource: purchase.paidBy || analysis.suggestedPaymentSource,
      paymentAccountCode: payAcct,
      accountCode: purchase.costAccountCode,
      vatRate: purchase.vatRate,
      reverseChargeCode: purchase.reverseChargeCode,
      supplierName: purchase.supplierName,
    },
  }), { sourceType: 'receipt_inbox', sourceId: entry.id, outcome: 'booked' });
  return { status: 'booked' };
};

const bookReadyReceiptInboxEntries = async (entryIds = [], options = {}) => {
  const plan = getReceiptInboxBookingPlan(entryIds, options);
  // Ett bilag som ble stoppet: si hvorfor. Flere: den generelle meldingen, siden
  // arsakene da er ulike og hoerer hjemme i oppsummeringen etterpaa.
  if (!plan.book.length) throw new Error(plan.skipped.length === 1 ? plan.skipped[0].reason : t('svc.noVouchersReady'));
  // `|| ''` for String(): uten den ville tre udefinerte alternativer gitt strengen
  // "undefined", som er truthy og ville blitt bokfort som en kontokode.
  const selectedPaymentAccount = String(options.paymentAccount || options.payAcct || options.paymentSource || '').trim();
  const booked = [], failed = [], skipped = [...plan.skipped];
  let remaining = [...plan.book];
  // Vanlige, klare kjopsbilag bruker samme atomiske backend-batch som AI Regnskapsforer.
  const batch = await tryBookReadyReceiptInboxEntriesBatch(remaining, selectedPaymentAccount);
  booked.push(...batch.booked); remaining = batch.remaining;
  // Feil paa ett bilag stopper ikke resten. For kastet lokka midt i, og da sto de
  // forste bilagene bokfort mens brukeren bare fikk en feilmelding om det siste.
  for (const planned of remaining) {
    const entry = receiptInboxBackendEntries().find((candidate) => candidate.id === planned.id);
    if (!entry) { skipped.push({ ...planned, kind: 'missing', reason: t('svc.bookSkipMissing') }); continue; }
    try {
      const result = await bookOneReceiptInboxEntry(entry, selectedPaymentAccount);
      if (result.status === 'booked') booked.push(planned);
      else skipped.push({ ...planned, kind: result.kind, reason: result.reason });
    } catch (error) {
      failed.push({ ...planned, reason: error?.message || t('svc.bookFailed') });
    }
  }
  return { booked, skipped, failed, model: getReceiptInboxModel() };
}; const documentInboxEntries = () => safeArray(runtime.appState?.documentationInbox);

const mapDocumentInboxToUi = (entry) => ({
  id: entry.id,
  name: entry.name || entry.attachmentName || 'Dokument',
  subject: entry.subject || entry.name || entry.attachmentName || 'Dokument',
  received: (entry.createdAt || '').slice(0, 10),
  createdAt: entry.createdAt,
  status: entry.status || 'active',
  trashedAt: entry.trashedAt,
  purgeAfter: entry.purgeAfter,
  file: entry.file || entry.downloadUrl || undefined,
  attachments: safeArray(entry.attachments).map((attachment) => ({
    id: attachment.id || attachment.storagePath || attachment.name,
    name: attachment.name || entry.name || 'Dokument',
    file: attachment.downloadUrl || attachment.file || entry.file,
    downloadUrl: attachment.downloadUrl || attachment.file || '',
    storagePath: attachment.storagePath || '',
    mimeType: attachment.mimeType || '',
  })).filter((attachment) => attachment.file || attachment.storagePath),
  _backend: entry,
});

const getDocumentationInboxModel = () => {
  const entries = documentInboxEntries()
    .map(mapDocumentInboxToUi)
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  return {
    entries,
    active: entries.filter((entry) => entry.status !== 'trashed'),
    trash: entries.filter((entry) => entry.status === 'trashed'),
  };
};

const uploadDocumentFileToInbox = async (file, partial = {}) => {
  if (!(file instanceof File)) throw new Error(t('svc.pickFileFirst'));
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const id = partial.id || `doc-${Date.now()}`;
  const cleanFileName = safeFilePart(file.name || partial.name || 'dokument');
  const storagePath = `businesses/${runtime.activeBusinessId}/documentationInbox/${id}/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      businessId: runtime.activeBusinessId,
      documentationInboxEntryId: id,
      source: 'plumme',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const attachment = stripUndefinedDeep({
    id: `att-${Date.now()}`,
    name: cleanFileName,
    mimeType: file.type || '',
    storagePath,
    downloadUrl: downloadUrl || undefined,
    uploadedAt: nowIso(),
  });
  const entry = stripUndefinedDeep({
    id,
    name: cleanFileName,
    attachmentName: cleanFileName,
    attachmentMimeType: file.type || '',
    file: downloadUrl || undefined,
    attachments: [attachment],
    source: partial.source || 'upload',
    status: 'active',
    createdAt: partial.createdAt || nowIso(),
    subject: partial.subject || cleanFileName,
  });
  await updateStateAndPersist((state) => ({
    ...state,
    documentationInbox: [entry, ...safeArray(state.documentationInbox).filter((item) => item.id !== id)],
  }));
  return mapDocumentInboxToUi(entry);
};

const moveDocumentationInboxEntriesToTrash = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  const trashedAt = nowIso();
  const purgeAfter = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  await updateStateAndPersist((state) => ({
    ...state,
    documentationInbox: safeArray(state.documentationInbox).map((entry) =>
      ids.has(entry.id) ? stripUndefinedDeep({ ...entry, status: 'trashed', trashedAt, purgeAfter, updatedAt: trashedAt }) : entry,
    ),
  }));
};

// Et inkassovarsel er et tillegg til den opprinnelige leverandørfakturaen, ikke et
// nytt kjøp. Kandidatene brukes bare til at brukeren kan knytte dokumentene sammen:
// vi autokobler aldri, siden samme leverandør kan ha flere fakturaer med lik sum.
const normalizedVoucherReference = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const findCollectionNoticeOriginalCandidates = ({ creditorName, creditorReference, principalAmount, noticeDate } = {}) => {
  const gross = Math.abs(num(principalAmount));
  const wantedSupplier = normalizedPartyName(creditorName);
  const wantedReference = normalizedVoucherReference(creditorReference);
  const latestDate = String(noticeDate || '').slice(0, 10);
  if (!(gross > 0) || (!wantedSupplier && !wantedReference)) return [];
  return safeArray(runtime.appState?.expenses)
    .filter((expense) => isActiveExpense(expense) && !expense.annulledAt)
    .filter((expense) => !latestDate || !expense.date || String(expense.date).slice(0, 10) <= latestDate)
    .filter((expense) => Math.abs(dashboardGrossExpense(expense) - gross) <= RECEIPT_DUPLICATE_AMOUNT_TOLERANCE)
    .map((expense) => {
      const reference = normalizedVoucherReference(expense.supplierInvoiceNumber || expense.invoiceNumber || expense.reference);
      const sameReference = Boolean(wantedReference && reference && wantedReference === reference);
      const sameSupplier = Boolean(wantedSupplier && normalizedPartyName(expense.supplierName) === wantedSupplier);
      return {
        id: expense.id,
        voucherNumber: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
        date: expense.date || '',
        year: Number(String(expense.date || '').slice(0, 4)) || null,
        grossAmount: dashboardGrossExpense(expense),
        supplierName: expense.supplierName || '',
        description: expense.description || '',
        sameReference,
        sameSupplier,
        score: (sameReference ? 2 : 0) + (sameSupplier ? 1 : 0),
      };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score || String(right.date).localeCompare(String(left.date)))
    .slice(0, 5);
};

const restoreDocumentationInboxEntries = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  await updateStateAndPersist((state) => ({
    ...state,
    documentationInbox: safeArray(state.documentationInbox).map((entry) =>
      ids.has(entry.id)
        ? stripUndefinedDeep({ ...entry, status: 'active', trashedAt: undefined, purgeAfter: undefined, updatedAt: nowIso() })
        : entry,
    ),
  }));
};

const deleteDocumentationInboxEntriesNow = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  await updateStateAndPersist((state) => ({
    ...state,
    documentationInbox: safeArray(state.documentationInbox).filter((entry) => !ids.has(entry.id)),
  }));
};

// Filsummen er det eneste som skiller «samme bilag en gang til» fra «to like
// kvitteringer fra samme butikk samme dag». `crypto.subtle` finnes ikke pa en
// usikker origin, og da lastes bilaget opp uten sum framfor aa feile.
const receiptFileContentHash = async (file) => {
  try {
    const digest = await window.crypto?.subtle?.digest?.('SHA-256', await file.arrayBuffer());
    if (!digest) return '';
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    return '';
  }
};

const uploadReceiptFileToInbox = async (file, partial = {}) => {
  if (!(file instanceof File)) throw new Error(t('svc.pickFileFirst'));
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  // Hasjen tas av originalen. Den er identiteten til «samme bilag en gang til»,
  // og maa ikke avhenge av hva komprimeringen gjorde med bytene.
  //
  // Selve opplastingen tar den nedskalerte fila. Et kamerabilde paa aatte
  // megabyte er det som gjor at brukeren staar og venter paa at bilaget skal
  // havne i innboksen; en kvittering paa 2200 piksler leses like godt.
  const [fileHash, uploadFile] = await Promise.all([
    receiptFileContentHash(file),
    Promise.resolve(window.PlummeReceiptImage?.prepareReceiptFileForUpload?.(file)).catch(() => file),
  ]);
  const storedFile = uploadFile || file;
  // Bilag kan lastes opp samtidig — kameraet slipper deg til neste bilde mens det
  // forrige gaar. `Date.now()` alene ga da to bilag samme id, og det andre skrev
  // over det forste.
  const id = partial.id || `receipt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const cleanFileName = safeFilePart(storedFile.name || file.name || partial.name || 'bilag');
  const storagePath = `businesses/${runtime.activeBusinessId}/receiptInbox/${id}/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(storedFile, {
    contentType: storedFile.type || 'application/octet-stream',
    customMetadata: {
      businessId: runtime.activeBusinessId,
      receiptInboxEntryId: id,
      source: 'plumme',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const attachment = stripUndefinedDeep({
    id: `att-${id}`,
    name: cleanFileName,
    mimeType: storedFile.type || '',
    storagePath,
    downloadUrl: downloadUrl || undefined,
    uploadedAt: nowIso(),
    fileHash: fileHash || undefined,
    fileSize: storedFile.size || undefined,
  });
  const paymentSourceDecision = normalizeReceiptPaymentSourceDecision(partial.paymentSourceDecision);
  const queuedAnalysis = applyReceiptPaymentSourceDecision({
    // `queued`, ikke `pending`: bilaget er lastet opp, men analysen har ikke
    // begynt paa det enna. Ved en bunke pa sytten bilag er forskjellen hele
    // forklaringen paa hvorfor raden staar stille.
    status: 'queued',
    supplierName: partial.supplierName || '',
    totalGross: partial.totalGross ?? null,
  }, paymentSourceDecision);
  const entry = stripUndefinedDeep({
    id,
    attachmentId: attachment.id,
    attachmentName: cleanFileName,
    attachmentMimeType: storedFile.type || '',
    fileHash: fileHash || undefined,
    fileSize: storedFile.size || undefined,
    file: downloadUrl || undefined,
    attachments: [attachment],
    source: partial.source || 'upload',
    status: partial.status || 'pending',
    createdAt: partial.createdAt || nowIso(),
    paymentSourceDecision: paymentSourceDecision || undefined,
    receiptAnalysis: queuedAnalysis,
    ...(window.PlummeProjectCostLink?.normalizeProjectCostFields(partial) || {}),
  });
  await updateStateAndPersist((state) => ({
    ...state,
    receiptInbox: [entry, ...safeArray(state.receiptInbox).filter((item) => item.id !== id)],
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return mapReceiptToUi(entry);
};

const mergeReceiptInboxEntries = async (sourceIds = [], targetId) => {
  const sourceSet = new Set(safeArray(sourceIds).filter((id) => id && id !== targetId));
  if (!targetId || !sourceSet.size) return null;
  let mergedUi = null;
  await updateStateAndPersist((state) => {
    const entries = safeArray(state.receiptInbox);
    const target = entries.find((entry) => entry.id === targetId);
    if (!target) return state;
    const sources = entries.filter((entry) => sourceSet.has(entry.id));
    const toAttachments = (entry) => {
      const fromAttachments = safeArray(entry.attachments);
      if (fromAttachments.length) return fromAttachments;
      return [stripUndefinedDeep({
        id: entry.attachmentId || entry.id,
        name: entry.attachmentName || 'Bilag',
        mimeType: entry.attachmentMimeType || '',
        downloadUrl: entry.file || '',
      })];
    };
    const attachments = [...toAttachments(target), ...sources.flatMap(toAttachments)];
    const merged = stripUndefinedDeep({
      ...target,
      attachmentName: target.attachmentName || attachments[0]?.name || 'Bilag',
      file: attachments[0]?.downloadUrl || target.file,
      attachments,
      updatedAt: nowIso(),
    });
    mergedUi = mapReceiptToUi(merged);
    return {
      ...state,
      receiptInbox: entries.filter((entry) => !sourceSet.has(entry.id)).map((entry) => entry.id === targetId ? merged : entry),
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return mergedUi;
};

const splitReceiptInboxEntry = async (entryId) => {
  let splitUi = [];
  await updateStateAndPersist((state) => {
    const entries = safeArray(state.receiptInbox);
    const index = entries.findIndex((entry) => entry.id === entryId);
    if (index < 0) return state;
    const entry = entries[index];
    const attachments = safeArray(entry.attachments);
    if (attachments.length < 2) return state;
    const parts = attachments.map((attachment, idx) => stripUndefinedDeep({
      ...entry,
      id: idx === 0 ? entry.id : `${entry.id}-part-${idx}-${Date.now()}`,
      attachmentId: attachment.id || `${entry.attachmentId || entry.id}-${idx}`,
      attachmentName: attachment.name || entry.attachmentName || 'Bilag',
      attachmentMimeType: attachment.mimeType || entry.attachmentMimeType || '',
      file: attachment.downloadUrl || entry.file,
      attachments: [attachment],
      receiptAnalysis: idx === 0 ? entry.receiptAnalysis : { ...(entry.receiptAnalysis || {}), totalGross: null, status: 'pending' },
      status: idx === 0 ? entry.status : 'pending',
      updatedAt: nowIso(),
    }));
    const next = [...entries];
    next.splice(index, 1, ...parts);
    splitUi = parts.map(mapReceiptToUi);
    return { ...state, receiptInbox: next };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return splitUi;
};

const supplierDisplayName = (supplier) => supplier?.name || supplier?.organizationNumber || supplier?.id || '';

const customerDisplayName = (customer) => customer?.name || customer?.organizationNumber || customer?.id || '';

const mergeCustomers = async (masterId, duplicateId) => {
  if (!masterId || !duplicateId || masterId === duplicateId) throw new Error(t('svc.pickTwoCustomers'));
  // Dialogene ligger i hver sin subcollection og folger ikke med state-sammenslaaingen
  // under. Flytt dem forst, saa en feil her skjer for kundene er slaatt sammen.
  const movedThreads = await moveCustomerConversationThreads(duplicateId, masterId);
  let mergedUi = [];
  await updateStateAndPersist((state) => {
    const customers = safeArray(state.customers);
    const master = customers.find((customer) => customer.id === masterId);
    const duplicate = customers.find((customer) => customer.id === duplicateId);
    if (!master || !duplicate) throw new Error(t('svc.customersToMergeNotFound'));
    const mergedCustomer = stripUndefinedDeep({
      ...master,
      customerNumber: master.customerNumber || duplicate.customerNumber,
      organizationNumber: master.organizationNumber || duplicate.organizationNumber,
      email: master.email || duplicate.email,
      phone: master.phone || duplicate.phone,
      address: master.address || duplicate.address,
      postalCode: master.postalCode || duplicate.postalCode,
      postalPlace: master.postalPlace || duplicate.postalPlace,
      crmStatus: master.crmStatus || duplicate.crmStatus,
      nextFollowUpDate: master.nextFollowUpDate || duplicate.nextFollowUpDate,
      note: master.note || duplicate.note,
      crmActivities: [...safeArray(master.crmActivities), ...safeArray(duplicate.crmActivities)],
      // Traadene er allerede flyttet i subcollectionen over. Legacy-feltet slaas
      // fortsatt sammen for kunder som ikke er migrert enda.
      conversationThreads: [...safeArray(master.conversationThreads), ...safeArray(duplicate.conversationThreads)],
      conversationSummary: movedThreads
        ? buildConversationSummary(movedThreads)
        : master.conversationSummary,
      updatedAt: nowIso(),
    });
    const archivedDuplicate = stripUndefinedDeep({
      ...duplicate,
      status: 'archived',
      archivedAt: duplicate.archivedAt || nowIso(),
      mergedIntoCustomerId: master.id,
      mergedIntoCustomerName: customerDisplayName(master),
    });
    const nextInvoices = safeArray(state.invoices).map((invoice) => {
      if (invoice.customerId !== duplicate.id && invoice.customer?.name !== duplicate.name) return invoice;
      return stripUndefinedDeep({
        ...invoice,
        customerId: master.id,
        customer: {
          ...(invoice.customer || {}),
          name: customerDisplayName(master),
          customerNumber: master.customerNumber || invoice.customer?.customerNumber,
          organizationNumber: master.organizationNumber || invoice.customer?.organizationNumber,
          email: master.email || invoice.customer?.email,
          phone: master.phone || invoice.customer?.phone,
          address: master.address || invoice.customer?.address,
          postalCode: master.postalCode || invoice.customer?.postalCode,
          postalPlace: master.postalPlace || invoice.customer?.postalPlace,
        },
      });
    });
    const nextCustomers = customers.map((customer) =>
      customer.id === master.id ? mergedCustomer : customer.id === duplicate.id ? archivedDuplicate : customer,
    );
    mergedUi = nextCustomers
      .filter((customer) => customer.status !== 'archived')
      .map((customer) => mapCustomerToUi(customer, { ...state, invoices: nextInvoices }));
    return { ...state, customers: nextCustomers, invoices: nextInvoices };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mergedUi;
};

const mergeSuppliers = async (masterId, duplicateId) => {
  if (!masterId || !duplicateId || masterId === duplicateId) throw new Error(t('svc.pickTwoSuppliers'));
  const result = await authorizedPost('supplierMergeHttp', {
    businessId: runtime.activeBusinessId,
    masterId,
    duplicateId,
    requestId: `supplier-merge-${masterId}-${duplicateId}`,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  const state = runtime.appState;
  const mergedUi = safeArray(state.suppliers).filter((supplier) => supplier.status !== 'archived').map((supplier) => mapSupplierToUi(supplier, state));
  window.dispatchEvent(new Event(BACKEND_EVENTS.suppliers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mergedUi;
};
